import { generatedFormattedDate } from "../../common/dateAndTime";
import { ERROR_MESSSAGE } from "../../config";

/**
 * Actions for get appointment module
 */
export const actions = {
  /**
   * For animating swiper
   *
   * @param {Object} context
   */
  toggleWeekAnimate(context) {
    setTimeout(() => {
      context.commit("toggleShowWeeks");
    }, 200); // If 200 is changed CSS corresponding CSS needs to be changed.
  },

  /**
   * Secure video login
   *
   * @param {*} context
   * @param {Number} appointmentID
   */
  async startVideoSession(context, appointmentID) {
    // Step 1: Start loading
    context.commit("setLoadingAddAppointment", true);

    /**
     * Step 2: Send the start session request
     *
     * {
     *   code: 200,
     *
     *   Format
     *   redirect: {
     *      LoginGuid: "16f859346b46456597985220be225738",
     *      LoginUri: "https://psybooksdemo.securevideo.com/Account/AutoLogin/16f859346b46456597985220be225738",
     *      RedirectToUriOnExpiry: "https://mobile-secure.psybooks.com"
     *   }
     * }
     */
    const json = await context.dispatch("makeAuthRequest", {
      url: `appointment/secure_video_login/${appointmentID}`,
      method: "GET"
    });

    // Step 3: Check if the redirect uri is sent and open the page
    if (json.redirect && json.redirect.LoginUri) {
      var url = window.open(json.redirect.LoginUri, "_blank");
      if (!url || url.closed || typeof url.closed == "undefined") {
        context.dispatch("globalMessagePopup", ERROR_MESSSAGE.popUpBlocker);
      }
    } else {
      context.dispatch("globalMessagePopup", ERROR_MESSSAGE.videoSessionLogin);
    }

    // Step 4: Stop loading
    context.commit("setLoadingAddAppointment", false);
  },

  /**
   * Fetching yearly appointments from server
   * These are only appointment dates
   *
   * @param {Object} context
   * @param {Number} year
   */
  async getYearlyAppointments(context, year = context.getters.getWeekYear) {
    // Getting the selected provider id
    const selectedProviderId = context.getters.getSelectedProviderID;

    // Not sending another query for the same year if query has already been sent before
    if (
      selectedProviderId === 0 ||
      (selectedProviderId in context.getters.getYearsQueried &&
        context.getters.getYearsQueried[selectedProviderId].indexOf(year) !==
          -1)
    ) {
      return false;
    }

    /**
     *  Sending the request
     *
     *  Format
     * {
     *   code: 200,
     *   appointments: String[] // Eg: [ "2020-03-02", "2020-01-02" ]
     * }
     */
    const json = await context.dispatch("makeAuthRequest", {
      url: "appointment/get/yearly/" + year + "/" + selectedProviderId,
      method: "GET"
    });

    if (!("error" in json)) {
      context.commit("concatYears", { json, selectedProviderId });
      context.commit("yearAdded", { year, selectedProviderId });
    }
  },

  /**
   * Fetching appointments of weeks
   * These are the actual appointment for the week
   *
   * @param {Object} context
   */
  async getAppointmentDetails(context) {
    // Getting the selected provider id
    const selectedProviderId = context.getters.getSelectedProviderID;

    // Getting the dates
    const currentWeek = context.getters.getSelectedWeek;
    const currentMonth = context.getters.getWeekMonth;
    const currentYear = context.getters.getWeekYear;

    // A local version of the state which only contains the appointment details of the dates for this pass
    const localAppointments = {};

    // Creating a formatted date
    const formattedDate = generatedFormattedDate(
      currentWeek,
      currentMonth + 1,
      currentYear
    );

    // Not sending query for weeks for which queries have already been sent
    if (
      selectedProviderId !== 0 &&
      (!(selectedProviderId in context.state.appointmentDatesAlreadyQueried) ||
        context.state.appointmentDatesAlreadyQueried[
          selectedProviderId
        ].indexOf(formattedDate) === -1)
    ) {
      // Start loader
      context.commit("increaseLoadingGetAppointment");

      // Step 1: Calculating day of the week
      const date = new Date(currentYear, currentMonth, currentWeek);
      const dayOfWeek = date.getDay();

      // Step 2: Generating start date
      const startDate = new Date(
        currentYear,
        currentMonth,
        currentWeek - dayOfWeek
      );

      // Step 3: Generating end date
      const endDate = new Date(
        currentYear,
        currentMonth,
        currentWeek + (6 - dayOfWeek)
      );

      /**
       * Send auth request
       *
       * Format
       * {
       *   code: 200, // The response code
       *   appointments: {
       *      app_date: "2020-08-30", // The appointment date
       *      app_end_hh: 3, // End time hour
       *      app_end_mm: 31, // End time minute
       *      app_end_tt: 0, // End time AM(0) or PM(1)
       *      app_oth_event: "", // For only this "event_appointment", this stores the name of the event
       *      app_start_hh: 2, // Start time hour
       *      app_start_mm: 46, // Start time minute,
       *      app_start_tt: 0 // Start time AM(0) or PM(1),
       *      app_type: 0 // Appointment type, client(0) and other(1)
       *      appointment_repeat_id: 388224 // If appointment type is repeat, this is the root appointment id
       *      appointmentrepeat_id: 0 // If this is a repeat appointment, then this is the appointment_repeat id
       *      client_id:3507 // Client id if it is a client appointment
       *      client_type:0 // Client type
       *      starts_from:"2020-08-18" // If repeat appointment, this is the start repeat date
       *      end_on:"2020-09-18" // If repeat appointment, this is the end repeat date
       *      id_note:null // Note id if there is any
       *      is_all_day:0 // If appointment is a repeat appointment. No(0), Yes(1)
       *      is_brokenrepeated:0 // If the appointment is a broken repeat. No(0), Yes(1)
       *      is_cancelled:0 // If the appointment is cancelled. No(0), Yes(1)
       *      is_deleted:0 // If the appointment is deleted. No(0), Yes(1)
       *      is_video:0 // If the appointment is video. No(0), Yes(1)
       *      firstName:"Hello" // The first name if any
       *      lastName:"World" // The last name if any
       *      middleName:"" // The middle name if any
       *      namePrefix:"" // Name prefix if any
       *      nameSuffix:"" // Name suffix if any
       *      notes:null // Notes if any present, or null
       *      open_note_id:null // The open note id
       *      otherevent_id:0 // The other event id, if this is an other event
       *      otherevent_name:null // The other event name like group name, person name or event_appointment name
       *      otherevent_type:0 // The other event type if the type is other event
       *      own_notes:null // The own notes if any
       *      provider_id:2213 // The provider id or doctor id
       *      reminder_set_status:0 // If there is any reminder status
       *      repeat:4 // The repeat type
       *      repeat_by:0 // The repeat by
       *      repeat_freq:1 // The repeat frequency
       *      repeat_on:"0,1" // Week days on which repeat
       *      repeats:1 // If it is a repeat appointment
       *      secure_video_session_id:null // The secure video id if any
       *      type:1 // Appointment type. Client(0), Other(1)
       *      video_appointment_id:null // Video appointment id if any
       *   }[]
       */
      const json = await context.dispatch("makeAuthRequest", {
        url: `appointment/get/daily/${startDate.getDate()}/${startDate.getMonth() +
          1}/${startDate.getFullYear()}/${endDate.getDate()}/${endDate.getMonth() +
          1}/${endDate.getFullYear()}/${selectedProviderId}`,
        method: "GET"
      });

      if (!("error" in json)) {
        const appointments = json.appointments;

        // Adding data to local state
        for (let i = 0; i < appointments.length; i++) {
          // Local state
          if (!(appointments[i]["app_date"] in localAppointments)) {
            localAppointments[appointments[i]["app_date"]] = [appointments[i]];
            context.commit("addDateToAlreadyQueriedDetails", {
              date: appointments[i]["app_date"],
              selectedProviderId
            });
          } else {
            localAppointments[appointments[i]["app_date"]].push(
              appointments[i]
            );
          }

          // Setting appointment detail
          context.commit("addDetails", appointments[i]);
        }

        // Update global state
        context.commit(
          "updateGlobalAppointmentsWithLocalAppointments",
          localAppointments
        );

        // Stop loader
        context.commit("decreaseLoadingGetAppointment");
      }
    }
  }
};
